import { UserReportResponse } from '@zond/api-interfaces';
import { useMemo } from 'react';
import { AttendancePeriodType, useAttendanceData } from '../../dashboard/hooks/useAttendanceData';
import { AttendanceAPIResponse } from '../../dashboard/types/attendance.types';

/**
 * Adapter for attendance data from reports
 * Converts data from reports format to the format expected by dashboard components
 */
function adaptAttendanceDataForReport(data?: UserReportResponse): AttendanceAPIResponse | undefined {
  if (!data?.attendance) return undefined;

  // Always use current_month for daily display regardless of period type
  const mappedPeriodType: AttendancePeriodType = 'current_month';

  return {
    users: data.attendance.users,
    records: data.attendance.records.map((record) => ({
      ...record,
      // Convert string status to the type expected by dashboard components
      status: record.status as 'present' | 'absent' | 'vacation' | 'sick' | 'remote',
    })),
    periodStart: data.attendance.periodStart,
    periodEnd: data.attendance.periodEnd,
    periodType: mappedPeriodType, // Use mapped period type
  };
}

/**
 * Hook for processing attendance data from reports
 * Reuses useAttendanceData logic from dashboard
 */
export function useReportAttendanceData(
  data?: UserReportResponse,
  selectedUsers: number[] = [],
  showAverageOnly: boolean = false,
  externalLoading: boolean = false
) {
  // Adapt report data to dashboard data format
  const adaptedData = useMemo(() => adaptAttendanceDataForReport(data), [data]);

  // Create mock useQuery result to pass to the original hook
  const mockUseQueryResult = useMemo(
    () => ({
      data: adaptedData,
      isLoading: externalLoading,
      error: null,
    }),
    [adaptedData, externalLoading]
  );

  // Reuse the original hook, replacing the useQuery call with our data
  return useAttendanceData(
    'current_month', // Use daily view format for all report types
    selectedUsers,
    showAverageOnly,
    undefined, // projectId is not used
    mockUseQueryResult // Pass our data instead of API call
  );
}
